import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Phone, Mic, Settings, Plus, Trash2, Edit, Play, PhoneCall,
  Loader2, CheckCircle2, XCircle, RefreshCw, Link2, Building2,
  Clock, Activity, BarChart3, AlertCircle, Key, Webhook
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOICES = [
  { id: '11labs-Adrian', label: 'Adrian (es-419)' },
  { id: '11labs-Matilda', label: 'Matilda (en-US)' },
  { id: 'openai-Nova', label: 'Nova (OpenAI)' },
  { id: 'openai-Alloy', label: 'Alloy (OpenAI)' },
  { id: 'deepgram-aura-asteria-en', label: 'Asteria Deepgram (en)' },
];

const LANGUAGES = [
  { id: 'es-419', label: 'Español (LATAM)' },
  { id: 'es-ES', label: 'Español (España)' },
  { id: 'en-US', label: 'English (US)' },
  { id: 'en-GB', label: 'English (UK)' },
  { id: 'fr-FR', label: 'Français' },
  { id: 'pt-BR', label: 'Português (Brasil)' },
  { id: 'de-DE', label: 'Deutsch' },
  { id: 'it-IT', label: 'Italiano' },
];

function CallStatusBadge({ status }) {
  const map = {
    registered: { label: 'Registrada', color: 'bg-blue-100 text-blue-700' },
    ongoing: { label: 'En curso', color: 'bg-green-100 text-green-700' },
    ended: { label: 'Terminada', color: 'bg-gray-100 text-gray-600' },
    error: { label: 'Error', color: 'bg-red-100 text-red-700' },
  };
  const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
}

function SecondsToDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AdminRetellAgents() {
  const [tab, setTab] = useState('config');
  const [configLoading, setConfigLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [configNotes, setConfigNotes] = useState('');
  const [configInfo, setConfigInfo] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showEditAgent, setShowEditAgent] = useState(false);
  const [showAssignAgent, setShowAssignAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [assignRestaurant, setAssignRestaurant] = useState('');

  const [phones, setPhones] = useState([]);
  const [phonesLoading, setPhonesLoading] = useState(false);

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showCallDetail, setShowCallDetail] = useState(false);

  const [agentForm, setAgentForm] = useState({
    agent_name: '', voice_id: '11labs-Adrian', language: 'es-419',
    general_prompt: '', begin_message: '', max_call_duration_ms: 3600000,
    end_call_after_silence_ms: 600000,
  });

  const adminToken = localStorage.getItem('admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` };

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/config`, { headers });
      if (r.ok) {
        const d = await r.json();
        setConfigInfo(d);
        setWebhookUrl(d.webhook_url || '');
        setConfigNotes(d.notes || '');
      }
    } catch {}
  }

  async function saveConfig() {
    if (!apiKey.trim()) { toast.error('Ingresa la API key de Retell'); return; }
    setConfigLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/config`, {
        method: 'POST', headers,
        body: JSON.stringify({ api_key: apiKey, webhook_url: webhookUrl, notes: configNotes }),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setApiKey(''); loadConfig(); }
      else toast.error(d.detail || 'Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setConfigLoading(false); }
  }

  async function testConnection() {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/test-connection`, { method: 'POST', headers });
      const d = await r.json();
      setTestResult(d);
      if (d.ok) toast.success(d.message); else toast.error(d.message);
    } catch { toast.error('Error de conexión'); }
    finally { setTesting(false); }
  }

  async function loadAgents() {
    setAgentsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/agents`, { headers });
      if (r.ok) { const d = await r.json(); setAgents(d.agents || []); }
      else toast.error('Error al cargar agentes');
    } catch { toast.error('Error de conexión'); }
    finally { setAgentsLoading(false); }
  }

  async function loadRestaurants() {
    try {
      const r = await fetch(`${API_URL}/api/admin/restaurants`, { headers });
      if (r.ok) { const d = await r.json(); setRestaurants(d.restaurants || d || []); }
    } catch {}
  }

  async function createAgent() {
    if (!agentForm.agent_name) { toast.error('Nombre requerido'); return; }
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/agents`, {
        method: 'POST', headers, body: JSON.stringify(agentForm),
      });
      if (r.ok) { toast.success('Agente creado'); setShowCreateAgent(false); loadAgents(); }
      else { const d = await r.json(); toast.error(d.detail || 'Error al crear agente'); }
    } catch { toast.error('Error de conexión'); }
  }

  async function updateAgent() {
    if (!selectedAgent) return;
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/agents/${selectedAgent.agent_id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({
          agent_name: agentForm.agent_name,
          voice_id: agentForm.voice_id,
          language: agentForm.language,
          general_prompt: agentForm.general_prompt,
          begin_message: agentForm.begin_message,
        }),
      });
      if (r.ok) { toast.success('Agente actualizado'); setShowEditAgent(false); loadAgents(); }
      else { const d = await r.json(); toast.error(d.detail || 'Error al actualizar'); }
    } catch { toast.error('Error de conexión'); }
  }

  async function deleteAgent(agentId) {
    if (!window.confirm('¿Eliminar este agente?')) return;
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/agents/${agentId}`, { method: 'DELETE', headers });
      if (r.ok) { toast.success('Agente eliminado'); loadAgents(); }
      else toast.error('Error al eliminar');
    } catch { toast.error('Error de conexión'); }
  }

  async function assignAgent() {
    if (!selectedAgent || !assignRestaurant) { toast.error('Selecciona un restaurante'); return; }
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/assign`, {
        method: 'POST', headers,
        body: JSON.stringify({ agent_id: selectedAgent.agent_id, restaurant_id: assignRestaurant }),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setShowAssignAgent(false); loadAgents(); }
      else toast.error(d.detail || 'Error al asignar');
    } catch { toast.error('Error de conexión'); }
  }

  async function loadPhones() {
    setPhonesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/phone-numbers`, { headers });
      if (r.ok) { const d = await r.json(); setPhones(Array.isArray(d) ? d : d.phone_numbers || []); }
      else toast.error('Error al cargar números');
    } catch { toast.error('Error de conexión'); }
    finally { setPhonesLoading(false); }
  }

  async function loadCalls() {
    setCallsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/calls?limit=50`, { headers });
      if (r.ok) { const d = await r.json(); setCalls(d.calls || []); }
      else toast.error('Error al cargar llamadas');
    } catch { toast.error('Error de conexión'); }
    finally { setCallsLoading(false); }
  }

  async function loadCallDetail(callId) {
    try {
      const r = await fetch(`${API_URL}/api/retell/admin/calls/${callId}`, { headers });
      if (r.ok) { setSelectedCall(await r.json()); setShowCallDetail(true); }
    } catch { toast.error('Error al cargar detalle'); }
  }

  function openEditAgent(agent) {
    setSelectedAgent(agent);
    setAgentForm({
      agent_name: agent.agent_name || '',
      voice_id: agent.voice_id || '11labs-Adrian',
      language: agent.language || 'es-419',
      general_prompt: agent.general_prompt || '',
      begin_message: agent.begin_message || '',
      max_call_duration_ms: agent.max_call_duration_ms || 3600000,
      end_call_after_silence_ms: agent.end_call_after_silence_ms || 600000,
    });
    setShowEditAgent(true);
  }

  function openAssignAgent(agent) {
    setSelectedAgent(agent);
    setAssignRestaurant(agent.assigned_restaurant_id || '');
    loadRestaurants();
    setShowAssignAgent(true);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Agente de Voz Retell AI</h1>
        <p className="text-sm text-gray-500 mt-1">Gestión de agentes de voz, números de teléfono y llamadas vía Retell API.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        if (v === 'agents' && agents.length === 0) loadAgents();
        if (v === 'phones') loadPhones();
        if (v === 'calls') loadCalls();
      }}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="config"><Settings className="h-4 w-4 mr-1.5" />Configuración</TabsTrigger>
          <TabsTrigger value="agents"><Mic className="h-4 w-4 mr-1.5" />Agentes</TabsTrigger>
          <TabsTrigger value="phones"><Phone className="h-4 w-4 mr-1.5" />Números</TabsTrigger>
          <TabsTrigger value="calls"><PhoneCall className="h-4 w-4 mr-1.5" />Llamadas</TabsTrigger>
        </TabsList>

        {/* ── CONFIG ── */}
        <TabsContent value="config" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-orange-500" />API Key de Retell</CardTitle>
              <CardDescription>Ingresa tu API key de <a href="https://retellai.com" target="_blank" rel="noreferrer" className="text-orange-500 underline">retellai.com</a> para activar la integración.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configInfo?.configured !== undefined && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${configInfo.api_key_masked ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {configInfo.api_key_masked ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {configInfo.api_key_masked ? `API key configurada: ${configInfo.api_key_masked}` : 'Sin configurar. Ingresa tu API key de Retell.'}
                </div>
              )}
              <div className="space-y-2">
                <Label>Nueva API Key</Label>
                <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder="key_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Webhook className="h-3.5 w-3.5" />Webhook URL (opcional)</Label>
                <Input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                  placeholder="https://tu-dominio.com/api/webhook/retell" />
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea value={configNotes} onChange={e => setConfigNotes(e.target.value)} rows={2} placeholder="Notas internas..." />
              </div>
              <div className="flex gap-3">
                <Button onClick={saveConfig} disabled={configLoading} className="bg-orange-500 hover:bg-orange-600">
                  {configLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Guardar API Key
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}
                  Probar Conexión
                </Button>
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mt-2 ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {testResult.message}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>¿Qué es Retell AI?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: Mic, title: 'Agentes de Voz', desc: 'Crea agentes de voz que atienden llamadas de reservaciones automáticamente.' },
                  { icon: Phone, title: 'Números Dedicados', desc: 'Asigna números de teléfono a restaurantes para atención automatizada.' },
                  { icon: BarChart3, title: 'Historial de Llamadas', desc: 'Revisa transcripciones y métricas de cada llamada.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── AGENTS ── */}
        <TabsContent value="agents" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{agents.length} agente(s) configurado(s)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadAgents} disabled={agentsLoading}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${agentsLoading ? 'animate-spin' : ''}`} />Actualizar
              </Button>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600"
                onClick={() => { setAgentForm({ agent_name: '', voice_id: '11labs-Adrian', language: 'es-419', general_prompt: '', begin_message: '', max_call_duration_ms: 3600000, end_call_after_silence_ms: 600000 }); setShowCreateAgent(true); }}>
                <Plus className="h-4 w-4 mr-1.5" />Nuevo Agente
              </Button>
            </div>
          </div>

          {agentsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : agents.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Mic className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay agentes de voz.</p>
              <p className="text-sm text-gray-400">Crea tu primer agente para gestionar llamadas de reservaciones.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agents.map(agent => (
                <Card key={agent.agent_id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{agent.agent_name || 'Agente sin nombre'}</CardTitle>
                        <CardDescription className="mt-0.5 font-mono text-xs">{agent.agent_id}</CardDescription>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {VOICES.find(v => v.id === agent.voice_id)?.label?.split(' ')[0] || agent.voice_id || 'Voz'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>🌐 {LANGUAGES.find(l => l.id === agent.language)?.label || agent.language || 'es-419'}</span>
                      <span>⏱ {Math.round((agent.max_call_duration_ms || 3600000) / 60000)} min máx</span>
                    </div>
                    {agent.general_prompt && (
                      <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 line-clamp-2">{agent.general_prompt}</p>
                    )}
                    {agent.assigned_restaurant_name ? (
                      <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                        <Building2 className="h-3.5 w-3.5" />{agent.assigned_restaurant_name}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 italic">Sin asignar a restaurante</div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => openEditAgent(agent)}>
                        <Edit className="h-3.5 w-3.5 mr-1" />Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openAssignAgent(agent)}>
                        <Link2 className="h-3.5 w-3.5 mr-1" />Asignar
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto"
                        onClick={() => deleteAgent(agent.agent_id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── PHONE NUMBERS ── */}
        <TabsContent value="phones" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{phones.length} número(s) registrado(s)</p>
            <Button variant="outline" size="sm" onClick={loadPhones} disabled={phonesLoading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${phonesLoading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>
          {phonesLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : phones.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Phone className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay números de teléfono.</p>
              <p className="text-sm text-gray-400 mt-1">Los números se gestionan desde el portal de Retell AI.</p>
              <a href="https://dashboard.retellai.com" target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="mt-4">Ir a Retell Dashboard</Button>
              </a>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {phones.map((pn, i) => (
                <Card key={i}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-lg font-semibold text-gray-900">{pn.phone_number}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{pn.nickname || 'Sin apodo'}</p>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        {pn.inbound_agent_id && <p>Entrante: <span className="font-mono">{pn.inbound_agent_id?.slice(0, 12)}...</span></p>}
                        {pn.outbound_agent_id && <p>Saliente: <span className="font-mono">{pn.outbound_agent_id?.slice(0, 12)}...</span></p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── CALLS ── */}
        <TabsContent value="calls" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{calls.length} llamada(s) reciente(s)</p>
            <Button variant="outline" size="sm" onClick={loadCalls} disabled={callsLoading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${callsLoading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>
          {callsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : calls.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <PhoneCall className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay llamadas registradas aún.</p>
            </CardContent></Card>
          ) : (
            <Card>
              <div className="divide-y">
                {calls.map(call => (
                  <div key={call.call_id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => loadCallDetail(call.call_id)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                        <PhoneCall className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {call.from_number || call.caller_id || 'Número desconocido'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {call.start_timestamp ? new Date(call.start_timestamp).toLocaleString('es-MX') : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {SecondsToDuration(call.duration_ms ? Math.round(call.duration_ms / 1000) : 0)}
                      </span>
                      <CallStatusBadge status={call.call_status} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ── CREATE AGENT DIALOG ── */}
      <Dialog open={showCreateAgent} onOpenChange={setShowCreateAgent}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Crear Agente de Voz</DialogTitle>
            <DialogDescription>Configura un nuevo agente de voz en Retell AI.</DialogDescription>
          </DialogHeader>
          <AgentFormFields form={agentForm} setForm={setAgentForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAgent(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={createAgent}>Crear Agente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EDIT AGENT DIALOG ── */}
      <Dialog open={showEditAgent} onOpenChange={setShowEditAgent}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Editar Agente de Voz</DialogTitle>
          </DialogHeader>
          <AgentFormFields form={agentForm} setForm={setAgentForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditAgent(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={updateAgent}>Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ASSIGN AGENT DIALOG ── */}
      <Dialog open={showAssignAgent} onOpenChange={setShowAssignAgent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Agente a Restaurante</DialogTitle>
            <DialogDescription>Vincula el agente "{selectedAgent?.agent_name}" a un restaurante.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Restaurante</Label>
              <Select value={assignRestaurant} onValueChange={setAssignRestaurant}>
                <SelectTrigger><SelectValue placeholder="Seleccionar restaurante..." /></SelectTrigger>
                <SelectContent>
                  {restaurants.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignAgent(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={assignAgent}>Asignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CALL DETAIL DIALOG ── */}
      <Dialog open={showCallDetail} onOpenChange={setShowCallDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Llamada</DialogTitle>
            <DialogDescription className="font-mono text-xs">{selectedCall?.call_id}</DialogDescription>
          </DialogHeader>
          {selectedCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium text-gray-600">Estado:</span> <CallStatusBadge status={selectedCall.call_status} /></div>
                <div><span className="font-medium text-gray-600">Duración:</span> {SecondsToDuration(selectedCall.duration_ms ? Math.round(selectedCall.duration_ms / 1000) : 0)}</div>
                <div><span className="font-medium text-gray-600">Desde:</span> {selectedCall.from_number || '—'}</div>
                <div><span className="font-medium text-gray-600">Hacia:</span> {selectedCall.to_number || '—'}</div>
              </div>
              {selectedCall.transcript && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Transcripción</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-2 max-h-60 overflow-y-auto">
                    {selectedCall.transcript_object?.map((msg, i) => (
                      <div key={i} className={`flex gap-2 ${msg.role === 'agent' ? 'text-orange-700' : 'text-gray-700'}`}>
                        <span className="font-semibold w-12 flex-shrink-0">{msg.role === 'agent' ? 'Agente' : 'Cliente'}:</span>
                        <span>{msg.content}</span>
                      </div>
                    )) || <p className="text-gray-500">{selectedCall.transcript}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AgentFormFields({ form, setForm }) {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Nombre del agente *</Label>
        <Input value={form.agent_name} onChange={e => setForm(f => ({ ...f, agent_name: e.target.value }))}
          placeholder="Agente Reservas — Restaurante X" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Voz</Label>
          <Select value={form.voice_id} onValueChange={v => setForm(f => ({ ...f, voice_id: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {VOICES.map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Idioma</Label>
          <Select value={form.language} onValueChange={v => setForm(f => ({ ...f, language: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Mensaje de bienvenida</Label>
        <Input value={form.begin_message} onChange={e => setForm(f => ({ ...f, begin_message: e.target.value }))}
          placeholder="¡Hola! Soy el asistente de reservaciones de..." />
      </div>
      <div className="space-y-2">
        <Label>Instrucciones del agente (prompt)</Label>
        <Textarea value={form.general_prompt} onChange={e => setForm(f => ({ ...f, general_prompt: e.target.value }))}
          rows={4} placeholder="Eres un asistente amable que ayuda a los clientes a hacer reservaciones en el restaurante. Pregunta por el nombre, fecha, hora y número de personas..." />
      </div>
    </div>
  );
}
